import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import config from 'config'

import useSnackbar from '../Snackbar/useSnackbar'

const useAssignmentCarChoice = () => {
  const { t } = useTranslation()
  const { showSnackbarMessage } = useSnackbar()

  const [state, setState] = useState({
    carChoice: {},
    isFetching: false,
    isUpdating: false,
    error: null,
  })

  const setStartFetching = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: true,
      isUpdating: false,
    }))
  }, [setState])

  const setStopFetching = useCallback(
    (error) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        error: error || null,
      }))
    },
    [setState]
  )

  const setStartUpdating = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: false,
      isUpdating: true,
    }))
  }, [setState])

  const setStopUpdating = useCallback(() => {
    setState((s) => ({
      ...s,
      isUpdating: false,
    }))
  }, [setState])

  const setSuccessFetching = useCallback(
    (data) => {
      setState((s) => ({
        ...s,
        carChoice: data,
        isFetching: false,
        isUpdating: false,
        error: null,
      }))
    },
    [setState]
  )

  const fetchCarChoice = useCallback(
    async (
      sessionId,
      plate,
      isUpdating = false,
      assignmentType = 'nonBlind'
    ) => {
      isUpdating ? setStartUpdating() : setStartFetching()

      try {
        const queryParams = new URLSearchParams({
          assignmentType,
        })
        const result = await axios.get(
          `${
            config.apiBaseUrl
          }sessions/${sessionId}/car-choice/${plate}?${queryParams.toString()}`
        )

        setSuccessFetching(result.data.body)
      } catch (e) {
        const message =
          e.response?.data?.message ||
          t('common.error.fetchData', {
            attribute: e.response?.status,
          })

        setStopFetching(e)

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [
      setStartFetching,
      setStartUpdating,
      setStopFetching,
      setSuccessFetching,
      showSnackbarMessage,
      t,
    ]
  )

  const assignCar = useCallback(
    async (
      sessionId,
      plate,
      partnerId,
      message,
      assignmentType = 'nonBlind'
    ) => {
      setStartUpdating()

      try {
        await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/assign-car`,
          { plate, message, partnerId, assignmentType }
        )

        setStopUpdating()
        fetchCarChoice(sessionId, plate, true, assignmentType)
      } catch (e) {
        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message: e.response?.data?.message,
        })
      }
    },
    [fetchCarChoice, setStartUpdating, setStopUpdating, showSnackbarMessage]
  )

  return {
    carChoice: state.carChoice,
    isFetching: state.isFetching,
    isUpdating: state.isUpdating,
    error: state.error,
    fetchCarChoice,
    assignCar,
  }
}

export default useAssignmentCarChoice
